import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../config/supabase';
import './ProfileSettings.css';

const ProfileSettings = () => {
  const { user, profile } = useAuth();

  /* ── Profile fields ── */
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');

  /* ── Password fields ── */
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  /* ── UI state ── */
  const [profileStatus, setProfileStatus] = useState(null); // { type: 'success'|'error', message }
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  /* Populate fields from context */
  useEffect(() => {
    if (user) setEmail(user.email || '');
    if (profile) {
      setFullName(profile.full_name || '');
      setRole(profile.role || '');
    }
  }, [user, profile]);

  /* ── Helpers ── */
  const formatRole = (raw) =>
    raw
      ? raw.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
      : '—';

  const getInitials = () => {
    const name = fullName || email || '';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return '?';
  };

  /* ── Save profile name ── */
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      setProfileStatus({ type: 'error', message: 'Full name cannot be empty.' });
      return;
    }

    setSavingProfile(true);
    setProfileStatus(null);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: fullName.trim() })
        .eq('id', user.id);

      if (error) throw error;

      setProfileStatus({ type: 'success', message: 'Profile updated successfully.' });
    } catch (err) {
      setProfileStatus({ type: 'error', message: err.message || 'Failed to update profile.' });
    } finally {
      setSavingProfile(false);
    }
  };

  /* ── Change password ── */
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordStatus(null);

    if (!newPassword || !confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'Please fill in all password fields.' });
      return;
    }
    if (newPassword.length < 8) {
      setPasswordStatus({ type: 'error', message: 'Password must be at least 8 characters.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'Passwords do not match.' });
      return;
    }

    setSavingPassword(true);

    try {
      /* Re-authenticate with current password first */
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (signInError) {
        setPasswordStatus({ type: 'error', message: 'Current password is incorrect.' });
        setSavingPassword(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setPasswordStatus({ type: 'success', message: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordStatus({ type: 'error', message: err.message || 'Failed to change password.' });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="ps-page">
      {/* ── Page header ── */}
      <div className="ps-page-header">
        <div>
          <h1 className="ps-page-title">Profile Settings</h1>
          <p className="ps-page-sub">Manage your personal account information and security.</p>
        </div>
      </div>

      <div className="ps-grid">

        {/* ── LEFT: Avatar + role card ── */}
        <aside className="ps-aside">
          <div className="ps-avatar-card">
            <div className="ps-avatar">{getInitials()}</div>
            <p className="ps-avatar-name">{fullName || email || 'User'}</p>
            <span className="ps-role-badge">{formatRole(role)}</span>
            <p className="ps-avatar-email">{email}</p>
          </div>

          <div className="ps-info-card">
            <h3 className="ps-info-title">Account Info</h3>
            <dl className="ps-info-list">
              <dt>Member since</dt>
              <dd>{user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) : '—'}</dd>
              <dt>Account ID</dt>
              <dd className="ps-id">{user?.id?.slice(0, 8)}…</dd>
            </dl>
          </div>
        </aside>

        {/* ── RIGHT: Forms ── */}
        <main className="ps-main">

          {/* Profile information */}
          <section className="ps-card">
            <div className="ps-card-header">
              <i className="bi bi-person-circle ps-card-icon" />
              <h2 className="ps-card-title">Personal Information</h2>
            </div>

            <form onSubmit={handleSaveProfile} className="ps-form" noValidate>
              <div className="ps-field-row">
                <div className="ps-field">
                  <label htmlFor="ps-fullname">Full Name</label>
                  <input
                    id="ps-fullname"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="ps-field">
                  <label htmlFor="ps-email">Email Address</label>
                  <div className="ps-input-with-icon">
                    <input
                      id="ps-email"
                      type="email"
                      value={email}
                      disabled
                      className="ps-disabled"
                    />
                    <i className="bi bi-lock-fill ps-lock-icon" title="Email cannot be changed here" />
                  </div>
                  <span className="ps-hint">Contact your administrator to change your email.</span>
                </div>
              </div>

              <div className="ps-field-row">
                <div className="ps-field">
                  <label htmlFor="ps-role">Role</label>
                  <input
                    id="ps-role"
                    type="text"
                    value={formatRole(role)}
                    disabled
                    className="ps-disabled"
                  />
                </div>
              </div>

              {profileStatus && (
                <div className={`ps-alert ps-alert--${profileStatus.type}`}>
                  <i className={`bi ${profileStatus.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'}`} />
                  {profileStatus.message}
                </div>
              )}

              <div className="ps-form-actions">
                <button type="submit" className="ps-btn ps-btn--primary" disabled={savingProfile}>
                  {savingProfile ? (
                    <><span className="ps-spinner" /> Saving…</>
                  ) : (
                    <><i className="bi bi-check-lg" /> Save Changes</>
                  )}
                </button>
              </div>
            </form>
          </section>

          {/* Change password */}
          <section className="ps-card">
            <div className="ps-card-header">
              <i className="bi bi-shield-lock ps-card-icon" />
              <h2 className="ps-card-title">Change Password</h2>
            </div>

            <form onSubmit={handleChangePassword} className="ps-form" noValidate>
              <div className="ps-field">
                <label htmlFor="ps-current-pw">Current Password</label>
                <input
                  id="ps-current-pw"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  autoComplete="current-password"
                />
              </div>

              <div className="ps-field-row">
                <div className="ps-field">
                  <label htmlFor="ps-new-pw">New Password</label>
                  <div className="ps-input-with-icon">
                    <input
                      id="ps-new-pw"
                      type={showNew ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="ps-toggle-pw"
                      onClick={() => setShowNew((v) => !v)}
                      aria-label={showNew ? 'Hide password' : 'Show password'}
                    >
                      <i className={`bi ${showNew ? 'bi-eye-slash' : 'bi-eye'}`} />
                    </button>
                  </div>
                </div>
                <div className="ps-field">
                  <label htmlFor="ps-confirm-pw">Confirm New Password</label>
                  <div className="ps-input-with-icon">
                    <input
                      id="ps-confirm-pw"
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="ps-toggle-pw"
                      onClick={() => setShowConfirm((v) => !v)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                    >
                      <i className={`bi ${showConfirm ? 'bi-eye-slash' : 'bi-eye'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Password strength indicator */}
              {newPassword.length > 0 && (
                <PasswordStrength password={newPassword} />
              )}

              {passwordStatus && (
                <div className={`ps-alert ps-alert--${passwordStatus.type}`}>
                  <i className={`bi ${passwordStatus.type === 'success' ? 'bi-check-circle' : 'bi-exclamation-circle'}`} />
                  {passwordStatus.message}
                </div>
              )}

              <div className="ps-form-actions">
                <button type="submit" className="ps-btn ps-btn--primary" disabled={savingPassword}>
                  {savingPassword ? (
                    <><span className="ps-spinner" /> Updating…</>
                  ) : (
                    <><i className="bi bi-shield-check" /> Update Password</>
                  )}
                </button>
              </div>
            </form>
          </section>
        </main>
      </div>
    </div>
  );
};

/* ── Password strength sub-component ── */
const PasswordStrength = ({ password }) => {
  const checks = [
    { label: 'At least 8 characters', pass: password.length >= 8 },
    { label: 'Uppercase letter', pass: /[A-Z]/.test(password) },
    { label: 'Number', pass: /\d/.test(password) },
    { label: 'Special character', pass: /[^A-Za-z0-9]/.test(password) },
  ];

  const score = checks.filter((c) => c.pass).length;
  const levels = ['', 'Weak', 'Fair', 'Good', 'Strong'];
  const colors = ['', '#e74c3c', '#f39c12', '#2ecc71', '#006B54'];

  return (
    <div className="ps-strength">
      <div className="ps-strength-bar">
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="ps-strength-segment"
            style={{ background: i <= score ? colors[score] : '#e0e0e0' }}
          />
        ))}
      </div>
      <span className="ps-strength-label" style={{ color: colors[score] }}>
        {levels[score]}
      </span>
      <ul className="ps-strength-checks">
        {checks.map((c) => (
          <li key={c.label} className={c.pass ? 'pass' : ''}>
            <i className={`bi ${c.pass ? 'bi-check-circle-fill' : 'bi-circle'}`} />
            {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ProfileSettings;
